import React, { PointerEventHandler } from 'react';
import cs from 'classnames';
import { injectStoresV2 } from '../../../../stores/injectStoresV2';
import { usePreventFocusOnClick } from '../../../../hooks/usePreventFocusOnClick';
import { BILoggerStore } from '../../../../stores/BILoggerStore';
import { getEditorLink, getViewerLink } from '../../../../../utils/templateLinks';
import { RoutingStore } from '../../../../stores/RoutingStore';
import { BaseURLStore } from '../../../../stores/BaseURLStore';
import { ConfigStore } from '../../../../stores/ConfigStore';
import { WithTranslations, withTranslations } from '../../../../../utils/withTranslations';
import { Template } from '../../../../../web-api/domain/template';
import { useEditorSessionId } from '../../../../contexts/EditorSessionIdContext';
import { hoverPanelDataHooks } from './HoverPanel.dataHooks';
import s from './HoverPanel.scss';

interface HoverPanelProps extends WithTranslations {
  biLoggerStore: BILoggerStore;
  routingStore: RoutingStore;
  baseURLStore: BaseURLStore;
  configStore: ConfigStore;
  template: Template;
  galleryDocIndex: number;
  className?: string;
  dataHook?: string;
  onInfoClick: () => void;
  isBestMatch?: boolean;
}

const HoverPanelCmp: React.FC<HoverPanelProps> = (props) => {
  const {
    template,
    t,
    galleryDocIndex,
    biLoggerStore,
    baseURLStore,
    routingStore,
    configStore,
    className,
    dataHook,
    onInfoClick,
    isBestMatch,
  } = props;
  const { editorSessionId, refreshEditorSessionId } = useEditorSessionId();
  const { siteId, metaSiteId, score } = template;
  const originUrl = baseURLStore.buildURL(routingStore.location);
  const { currentLanguage } = configStore.config;
  const { petri_ovr, dashboardFirstMetaSiteId, siteCreationFolderId, dashboardFirst } =
    routingStore.getGlobalUrlParams();
  const isOpenTemplateInSameTab = !!dashboardFirstMetaSiteId;

  const editorLink = getEditorLink({
    template,
    autoDetectCategory: configStore.config.autoDetectCategory,
    editorSessionId,
    petri_ovr,
    dashboardFirstMetaSiteId,
    siteCreationFolderId,
    dashboardFirst,
  });
  const viewerLink = getViewerLink({
    templateSlug: template.id,
    language: currentLanguage,
    originUrl,
    editorSessionId,
    dashboardFirstMetaSiteId,
    dashboardFirst,
  });

  const handleEditButtonClick = () => {
    void biLoggerStore.logTemplateEdit({
      siteId,
      galleryDocIndex,
      openingBrowserUrl: editorLink,
      metaSiteId,
      editorSessionId,
      origin: 'create-page',
      score,
      currentPageNumber: routingStore.currentPage,
      isBestMatch,
    });

    setTimeout(() => refreshEditorSessionId());
  };

  const logBITemplateView = (previewType: 'new_tab' | 'modal') => {
    biLoggerStore.logTemplateView({
      siteId,
      galleryDocIndex,
      openingBrowserUrl: viewerLink,
      origin: 'thumbnail' as const,
      score,
      currentPageNumber: routingStore.currentPage,
      buttonName: 'view',
      isBestMatch,
      previewType,
    });

    setTimeout(() => refreshEditorSessionId());
  };

  const handlePreviewClick: PointerEventHandler<HTMLAnchorElement> = () => {
    logBITemplateView('new_tab');
  };

  const handleShowInfoKeyPress = (event: React.KeyboardEvent) => {
    if (['Enter', 'Space'].includes(event.nativeEvent.code)) {
      event.preventDefault();
      onInfoClick();
    }
  };

  const preventFocusOnClick = usePreventFocusOnClick();

  const editButton = (
    <a
      href={editorLink}
      target={isOpenTemplateInSameTab ? '_self' : '_blank'}
      className={cs(s.editBtn, {
        [s.newEditBtn]: true,
      })}
      data-hook={hoverPanelDataHooks.editorLink()}
      onClick={handleEditButtonClick}
      onFocus={preventFocusOnClick}
      role="button"
    >
      {dashboardFirst === 'true' ? t('template.choose') : t('template.edit')}
    </a>
  );

  const viewButton = (
    <a
      href={viewerLink}
      target={isOpenTemplateInSameTab ? '_self' : '_blank'}
      className={cs(s.previewBtn, s.newPreviewBtn)}
      data-hook={hoverPanelDataHooks.previewLink()}
      onClick={handlePreviewClick}
      onFocus={preventFocusOnClick}
      role="button"
      aria-haspopup="true"
    >
      {t('template.view')}
    </a>
  );

  return (
    <div className={cs(s.hoverPanel, className, { [s.newHoverPanel]: true })} data-hook={dataHook}>
      <div className={cs(s.buttons, { [s.newButtons]: true })}>
        {editButton}
        {viewButton}
      </div>
      <div
        className={s.showInfo}
        data-hook={hoverPanelDataHooks.showIconLabel()}
        aria-label={t('template.showInfo')}
        role="button"
        onKeyDown={handleShowInfoKeyPress}
        tabIndex={0}
        onFocus={preventFocusOnClick}
        onClick={onInfoClick}
      >
        {t('template.info')}
      </div>
    </div>
  );
};

export const HoverPanel = withTranslations(
  injectStoresV2('biLoggerStore', 'routingStore', 'baseURLStore', 'configStore')(HoverPanelCmp),
);
